// src/lib/eventCandidates.js
// Phase 2 of the smart-composer redesign — fetch a small set of "obvious"
// events the coach is probably trying to message about, so the wizard can
// offer them as one-tap pre-fills at the top of the details step.
//
// Phase 1 handled the slide-over case ("I'm looking at THIS event"). Phase 2
// is the cold-start ("I want to send a message — pick the event for me").
//
// What counts as a "candidate" depends on the message type:
//   • Game Recap     → past games (last 21d) WITH a posted score
//   • Reminder       → upcoming events (next 21d), not cancelled
//   • Cancellation   → upcoming events (next 21d), not cancelled (so coach
//                       can call one OFF — only makes sense on live ones)
//   • Schedule Change → same as Reminder (move an upcoming event)
//   • Other types (Payment Due, Motivation, Tryout Info, Season Wrap)
//                    → no event-based prefill makes sense; return empty.
//
// Firestore rules note: under the Phase 2 lockdown, `games` reads must
// include a `where('teamId', '==', X)` clause. We query per-team and
// merge in memory rather than trying to do a single multi-team query
// (Firestore `in` queries also work but are capped at 30 values and
// don't compose with other equality filters cleanly).

import {
  collection, query, where, getDocs, doc, getDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';

/** Safe Firestore Timestamp → JS Date. */
function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Project an event doc down to just the fields the picker / prefill needs.
 * Keeps the returned candidates small and stable across query shapes.
 */
function shapeCandidate(d, teamId, _teamName) {
  const data = d.data ? d.data() : d;
  return {
    id: d.id || data.id,
    teamId,
    eventType: data.eventType || 'game',
    date: data.date || null,
    opponent: data.opponent || '',
    fieldName: data.fieldName || '',
    fieldAddress: data.fieldAddress || '',
    homeScore: data.homeScore ?? null,
    awayScore: data.awayScore ?? null,
    homeAway: data.homeAway || null,
    status: data.status || null,
    seasonId: data.seasonId || null,
    seriesId: data.seriesId || null,
    title: data.title || '',
    _teamName: _teamName || null,
  };
}

/** Pull a team's display name; tolerant of read failures (returns null). */
async function fetchTeamName(teamId) {
  try {
    const snap = await getDoc(doc(db, 'teams', teamId));
    if (snap.exists()) return snap.data()?.name || null;
  } catch (_e) { /* swallow — name is cosmetic */ }
  return null;
}

/**
 * The single function the wizard calls. Returns up to `max` candidates,
 * shaped + sorted appropriately for the message type.
 *
 * Returns [] for any unknown / non-prefillable type. Returns [] (not throws)
 * if the underlying queries fail — the caller falls back to a manual form.
 */
export async function fetchEventCandidatesForMessageType({ teamIds, messageType, max = 8 }) {
  if (!Array.isArray(teamIds) || teamIds.length === 0) return [];
  if (!messageType) return [];

  // Filter type to predicate + sort direction.
  let predicate = null;
  let sortDir = 'asc'; // 'asc' for upcoming, 'desc' for past
  const now = Date.now();
  const horizon = 21 * DAY_MS;

  switch (messageType) {
    case 'Game Recap': {
      sortDir = 'desc';
      predicate = (e) => {
        if (e.eventType !== 'game') return false;
        if (e.homeScore == null || e.awayScore == null) return false;
        const t = toDate(e.date)?.getTime();
        if (!t) return false;
        return t <= now && (now - t) <= horizon;
      };
      break;
    }
    case 'Reminder':
    case 'Cancellation':
    case 'Schedule Change': {
      sortDir = 'asc';
      predicate = (e) => {
        if (e.status === 'cancelled') return false;
        const t = toDate(e.date)?.getTime();
        if (!t) return false;
        return t >= now && (t - now) <= horizon;
      };
      break;
    }
    default:
      // Payment Due / Motivation / Tryout Info / Season Wrap — no
      // event-based prefill is useful. Bail early so we don't burn reads.
      return [];
  }

  // If we have multiple teams, look up names so the picker can disambiguate
  // ("vs FC United · Lions U12"). Single-team callers don't need this.
  const needsTeamNames = teamIds.length > 1;
  const teamNamesById = {};
  if (needsTeamNames) {
    await Promise.all(
      teamIds.map(async (id) => {
        teamNamesById[id] = await fetchTeamName(id);
      })
    );
  }

  // Per-team queries. Phase 2 rules require teamId equality, so we
  // can't combine them server-side. In-memory merge + dedupe handles
  // the rare case where a doc somehow surfaces under two team queries.
  const perTeamResults = await Promise.all(
    teamIds.map(async (teamId) => {
      try {
        const snap = await getDocs(
          query(collection(db, 'games'), where('teamId', '==', teamId))
        );
        return snap.docs.map((d) => shapeCandidate(d, teamId, teamNamesById[teamId]));
      } catch (err) {
        // Don't kill the whole picker because one team's read failed
        // (e.g. coach lost staff status on one of N teams in a club).
        if (import.meta.env?.DEV) {
          console.warn('[eventCandidates] team', teamId, 'query failed', err);
        }
        return [];
      }
    })
  );

  // Flatten + dedupe by id (compound team queries shouldn't overlap, but
  // belt + braces in case the same teamId appears twice in the input).
  const seen = new Set();
  const merged = [];
  for (const list of perTeamResults) {
    for (const c of list) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      if (predicate(c)) merged.push(c);
    }
  }

  // Sort by date (asc for upcoming, desc for past).
  merged.sort((a, b) => {
    const ta = toDate(a.date)?.getTime() || 0;
    const tb = toDate(b.date)?.getTime() || 0;
    return sortDir === 'asc' ? ta - tb : tb - ta;
  });

  return merged.slice(0, max);
}
